import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_NAME_DISPLAY,
  resolveDisplayName,
  type NameDisplayMode,
  type Presence,
  type PublicUser,
} from '@avslack/shared';
import { listUsers } from '../lib/api';
import { useSession } from '../lib/session';
import { withOfflineFallback } from '../lib/offline-cache';

export const usersKey = ['users'] as const;

export function useUsers() {
  const query = useQuery({
    queryKey: usersKey,
    // Terero nelkul az utolso ismert lista, kulonben minden szerzo "ismeretlen" (K2).
    queryFn: withOfflineFallback('users', listUsers),
    staleTime: 60_000,
  });
  const session = useSession();

  /**
   * Neveket vagy beceneveket latunk?
   *
   * A dontes a NEZOE, nem a nev tulajdonosae - ezert a sajat rekordunkbol
   * jon, nem abbol, akit eppen nezunk.
   */
  const mode: NameDisplayMode = session.data?.user.nameDisplay ?? DEFAULT_NAME_DISPLAY;

  const usersById = useMemo(() => {
    const map = new Map<string, PublicUser>();
    for (const user of query.data?.users ?? []) map.set(user.id, user);
    return map;
  }, [query.data]);

  /**
   * A megjelenitendo nev.
   *
   * Mindenhol EZEN keresztul irjuk ki a neveket, nem a `displayName`
   * kozvetlen olvasasaval - kulonben a becenev-beallitas felig mukodne, ami
   * rosszabb, mintha egyaltalan nem lenne.
   *
   * Kivetel az admin felulet: ott a fiokot kezeljuk, tehat a rendes nev es az
   * e-mail cim a lenyeg, nem az, hogy a kollegak hogy hivjak egymast.
   */
  const nameOf = useMemo(() => {
    return (user: PublicUser | string | undefined | null, fallback = 'Ismeretlen'): string => {
      const found = typeof user === 'string' ? usersById.get(user) : user;
      if (!found) return fallback;
      return resolveDisplayName(found, mode);
    };
  }, [usersById, mode]);

  return {
    ...query,
    users: query.data?.users ?? [],
    usersById,
    nameOf,
    nameDisplay: mode,
    presence: (query.data?.presence ?? {}) as Record<string, Presence>,
  };
}
